import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismLoadingBar,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import HotelOverviewDashboardFeedFeedEntry from 'lumen/components/hotel/overview-dashboard/feed/feed-entry';
import { DASHBOARD_FEED_SHOW_FILTER_KEYS } from 'lumen/utils/constants/overview-dashboard-constants';

interface HotelOverviewDashboardFeedDashboardFeedSignature {
  Args: {
    alertsResponseData: unknown;
    dashboardFeedData: unknown;
    dashboardFeedSelectedShowType: unknown;
    isAlertsRequestLoading: unknown;
    numberOfCompetitors: unknown;
    onKeyDateStarClicked: unknown;
    openAddKeyDateModal: unknown;
    relaunchAlertsDataRequest: unknown;
    toggleOrSetSelectedDay: unknown;
    triggerDayDrilldownOutsideSelectedRange: unknown;
    updateKeyDateNoteTask: unknown;
  };
}

export default class HotelOverviewDashboardFeedDashboardFeed extends Component<HotelOverviewDashboardFeedDashboardFeedSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get alertsEmptyStateMessage() {
    if (this.hasCutOffFromDateRangeEndMessage) {
      return this.dateRangeCutOffMessage;
    }

    // Date range is not cut off, but there are just no alerts
    return this.intl.t('overview_dashboard.feed.empty_state_alerts.title');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get alertsResponseMeta() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.alertsResponseData?.meta;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateRangeCutOffMessage() {
    if (this.hasCutOffFromDateRangeEndMessage) {
      return this.intl.t(
        'overview_dashboard.cutoff_from_date_range_end.message',
        {
          // Personalise the cut off message depending on the shopconfig of the subscription
          // Need to subtract 1 from the shop_length. We'll normally receive 91 or 61, but we need to show 90 / 60 to the user
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          shopLength: this.alertsResponseMeta.shop_length - 1,
        },
      );
    }

    return null;
  }

  // Alerts are generated for the next 60 / 90 days (depends on the daily shops of the subscription)
  // Check if the requested daterange is cut off happens in BE, so we check here in the meta of that response if we need to show that message or not
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCutOffFromDateRangeEndMessage() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.alertsResponseMeta?.has_cutoff_from_date_range_end;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showAlertsEmptyState() {
    return (
      !this.args.isAlertsRequestLoading &&
      // @ts-expect-error: Incorrect type
      !this.args.dashboardFeedData.length &&
      // @ts-expect-error: Incorrect type
      this.args.dashboardFeedSelectedShowType.id ===
        DASHBOARD_FEED_SHOW_FILTER_KEYS.ALERTS
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showFavouritesEmptyState() {
    return (
      !this.args.isAlertsRequestLoading &&
      // @ts-expect-error: Incorrect type
      !this.args.dashboardFeedData.length &&
      // @ts-expect-error: Incorrect type
      (this.args.dashboardFeedSelectedShowType.id ===
        DASHBOARD_FEED_SHOW_FILTER_KEYS.FAVOURITES ||
        // @ts-expect-error: Incorrect type
        this.args.dashboardFeedSelectedShowType.id ===
          DASHBOARD_FEED_SHOW_FILTER_KEYS.ALL)
    );
  }

  <template>
    <div class="od-feed-tab-scrollable-container-wrapper">
      {{#if @isAlertsRequestLoading}}
        <PrismLoadingBar />
      {{/if}}
      <div class="od-feed-tab-scrollable-container-wrapper__content">
        {{! @glint-expect-error: Incorrect type }}
        {{#if @dashboardFeedData.length}}
          {{! @glint-expect-error: Incorrect type }}
          {{#each @dashboardFeedData as |data|}}
            <HotelOverviewDashboardFeedFeedEntry
              @data={{data}}
              @isAlertsRequestLoading={{@isAlertsRequestLoading}}
              {{! @glint-expect-error: Incorrect type }}
              @numberOfCompetitors={{@numberOfCompetitors}}
              @onKeyDateStarClicked={{@onKeyDateStarClicked}}
              @relaunchAlertsDataRequest={{@relaunchAlertsDataRequest}}
              @toggleOrSetSelectedDay={{@toggleOrSetSelectedDay}}
              @triggerDayDrilldownOutsideSelectedRange={{@triggerDayDrilldownOutsideSelectedRange}}
              @updateKeyDateNoteTask={{@updateKeyDateNoteTask}}
            />
          {{/each}}
          {{! We add some extra whitespace at the end of all feed entries to make sure intercom button doesn't overlap the last feedEntry }}
          <div class="od-feed-entry-extra-whitespace"></div>
        {{else}}
          {{#if this.showFavouritesEmptyState}}
            <div class="flex justify-center">
              <PrismMessage
                @description={{t
                  "overview_dashboard.feed.empty_state.subtitle"
                }}
                @illustration="100x100_critical"
                @title={{t "overview_dashboard.feed.empty_state.title"}}
              />
            </div>
            <div class="mt-400 flex justify-center">
              <PrismButton
                @iconBefore="plus"
                @label={{t "overview_dashboard.key_date.button.add_key_date"}}
                {{! @glint-expect-error: Incorrect type }}
                @onClick={{@openAddKeyDateModal}}
              />
            </div>
          {{else if this.showAlertsEmptyState}}
            <div class="flex justify-center">
              <PrismMessage
                @description={{t
                  "overview_dashboard.feed.empty_state_alerts.paragraph"
                }}
                @illustration="100x100_critical"
                {{! @glint-expect-error: Incorrect type }}
                @title={{this.alertsEmptyStateMessage}}
              />
            </div>
          {{/if}}
        {{/if}}
      </div>
    </div>
  </template>
}
